/**
 * MeetingHistorySection — Plaud recordings panel for AccountWorkspace.
 *
 * Features:
 * - Lists recordings linked to current account with date grouping
 * - "Import from Plaud" button to sync new recordings
 * - Expands to show summary and transcript
 *
 * Data: GET /api/plaud/account/:accountId
 * Import: POST /api/plaud/sync (via PlaudImportModal)
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Mic, Plus, RefreshCw, AlertCircle, Volume2, ChevronDown, ChevronUp } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import PlaudRecordingCard from './PlaudRecordingCard';
import PlaudImportModal from './PlaudImportModal';
import PlaudTranscriptModal from './PlaudTranscriptModal';
import './PlaudRecordings.css';

// Categorize date into groups
function getDateGroup(dateStr) {
  if (!dateStr) return 'Earlier';

  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const recordDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const diffDays = Math.floor((today - recordDate) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return 'This Week';
  if (diffDays <= 30) return 'This Month';
  return 'Earlier';
}

// Group recordings by date
function groupByDate(recordings) {
  const groups = {
    'Today': [],
    'Yesterday': [],
    'This Week': [],
    'This Month': [],
    'Earlier': [],
  };

  recordings.forEach(recording => {
    const group = getDateGroup(recording.recorded_at);
    groups[group].push(recording);
  });

  // Return only non-empty groups
  return Object.entries(groups)
    .filter(([_, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

function MeetingHistorySection({ accountId, accountName, contacts = [], collapsed = false }) {
  const [showImport, setShowImport] = useState(false);
  const [viewingTranscript, setViewingTranscript] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['plaud-recordings', accountId],
    queryFn: () => apiClient.get(`/api/plaud/account/${accountId}`),
    enabled: !!accountId,
    staleTime: 60_000,
  });

  const recordings = data?.recordings || [];
  const count = recordings.length;

  // Group recordings by date
  const groupedRecordings = useMemo(() => groupByDate(recordings), [recordings]);

  // Calculate how many to show initially (first 5)
  const INITIAL_LIMIT = 5;
  const hasMore = count > INITIAL_LIMIT;

  const handleImportSuccess = () => {
    setShowImport(false);
    queryClient.invalidateQueries({ queryKey: ['plaud-recordings', accountId] });
  };

  if (collapsed) {
    return (
      <div className="aw-plaud-section aw-plaud-collapsed">
        <div className="aw-plaud-section-header">
          <Volume2 size={15} />
          <span className="aw-plaud-section-title">Meeting Recordings</span>
          <span className="aw-plaud-section-count">{count}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="aw-plaud-section">
      <div className="aw-plaud-section-header">
        <Volume2 size={15} />
        <span className="aw-plaud-section-title">Meeting Recordings</span>
        <span className="aw-plaud-section-count">{count}</span>
        <div className="aw-plaud-section-actions">
          <button
            className="aw-plaud-refresh"
            onClick={() => refetch()}
            title="Refresh recordings"
            disabled={isLoading}
          >
            <RefreshCw size={14} className={isLoading ? 'aw-spin' : ''} />
          </button>
          <button
            className="aw-plaud-import-btn"
            onClick={() => setShowImport(true)}
            title="Import from Plaud"
          >
            <Plus size={14} /> Import
          </button>
        </div>
      </div>

      <div className="aw-plaud-section-body">
        {isLoading && !recordings.length && (
          <p className="aw-plaud-loading">Loading recordings...</p>
        )}

        {error && (
          <div className="aw-plaud-error">
            <AlertCircle size={14} />
            <span>
              {/* A bare "Failed to load recordings" reads the same whether the
                  backend is asleep, the session expired, or the account has no
                  recordings endpoint at all — and sent us chasing an endpoint
                  that turned out to be healthy. Name the reason. */}
              {error.status === 401 || error.status === 403
                ? 'Not authorised to read recordings — sign in again.'
                : error.status === undefined || error.status >= 500
                  ? "Couldn't reach the recordings service."
                  : `Couldn't load recordings (${error.status}).`}
            </span>
            <button type="button" className="aw-plaud-retry" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && recordings.length === 0 && (
          <div className="aw-plaud-empty">
            <Mic size={24} strokeWidth={1.5} />
            <p>No meeting recordings yet</p>
            <button
              className="aw-plaud-empty-btn"
              onClick={() => setShowImport(true)}
            >
              <Plus size={14} /> Import from Plaud
            </button>
          </div>
        )}

        {/* Date-grouped recordings */}
        {groupedRecordings.map((group) => {
          // Calculate how many to show from this group
          let itemsToShow = group.items;
          if (!showAll && hasMore) {
            // Only limit when not showing all
            const currentShown = groupedRecordings
              .slice(0, groupedRecordings.indexOf(group))
              .reduce((acc, g) => acc + g.items.length, 0);

            if (currentShown >= INITIAL_LIMIT) {
              return null; // Skip this group entirely
            }

            const remaining = INITIAL_LIMIT - currentShown;
            itemsToShow = group.items.slice(0, remaining);
          }

          return (
            <div key={group.label} className="aw-plaud-date-group">
              <div className="aw-plaud-date-header">{group.label}</div>
              {itemsToShow.map((recording) => (
                <PlaudRecordingCard
                  key={recording.id}
                  recording={recording}
                  contacts={contacts}
                  onViewTranscript={setViewingTranscript}
                />
              ))}
            </div>
          );
        })}

        {/* Show more/less toggle */}
        {hasMore && (
          <button
            className="aw-plaud-toggle-more"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                <ChevronUp size={14} /> Show less
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Show all {count} recordings
              </>
            )}
          </button>
        )}
      </div>

      {showImport && (
        <PlaudImportModal
          accountId={accountId}
          accountName={accountName}
          onClose={() => setShowImport(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      {viewingTranscript && (
        <PlaudTranscriptModal
          recording={viewingTranscript}
          contacts={contacts}
          onClose={() => setViewingTranscript(null)}
          onContactsLinked={() => {
            queryClient.invalidateQueries({ queryKey: ['plaud-recordings', accountId] });
          }}
        />
      )}
    </div>
  );
}

export default MeetingHistorySection;
